import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useConfirm } from "../lib/confirm";
import { Plus, Trash2, X, Key, SlidersHorizontal } from "lucide-react";
import JurisdictionLodgesPanel from "../components/JurisdictionLodgesPanel";

// Default role of created accounts is ALWAYS "admin" — the Users page is
// dedicated to WEB users (lodge officers / staff). Mobile-only members
// have their own dedicated management surface and must NOT appear here.
const emptyForm = { username: "", password: "", name: "", role: "admin", office_role: "wm", permissions: [] };

// Administrative roles only (per user request): MV/MM = full admin;
// Secretar/Trezorier/Mare Secretar/Mare Trezorier = admin with ticked
// module permissions, enforced centrally by the backend.
const SECRETARY_PERMS = ["members", "events", "minutes", "documents", "certificates", "announcements", "messages", "petitions", "transfers", "mobile_app", "office_history", "votes"];
const TREASURER_PERMS = ["dues", "members_view"];
const ALL_PERMS = [...SECRETARY_PERMS, ...TREASURER_PERMS];
const ROLE_DEFAULTS = {
  secretary: SECRETARY_PERMS,
  treasurer: TREASURER_PERMS,
  grand_secretary: SECRETARY_PERMS,
  grand_treasurer: TREASURER_PERMS,
};
const OFFICE_ROLES = ["wm", "secretary", "treasurer", "grand_master", "grand_secretary", "grand_treasurer"];
const LIMITED_ROLES = ["secretary", "treasurer", "grand_secretary", "grand_treasurer"];
const ROLE_LABEL_KEYS = {
  wm: "users.roleWm",
  secretary: "users.roleSecretary",
  treasurer: "users.roleTreasurer",
  grand_master: "users.roleGrandMaster",
  grand_secretary: "users.roleGrandSecretary",
  grand_treasurer: "users.roleGrandTreasurer",
};

export default function UsersPage() {
  const { t, i18n } = useTranslation();
  const locale =t("users.en_gb");
  const { user } = useAuth();
  const confirm = useConfirm();
  const canManageUsers = user?.role === "admin" && !user?.child_lodge_id;
  const [users, setUsers] = useState([]);
  const [error, setError] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [resetFor, setResetFor] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [permsFor, setPermsFor] = useState(null);
  const [permsDraft, setPermsDraft] = useState([]);
  const [permsSaving, setPermsSaving] = useState(false);

  const savePerms = async (e) => {
    e.preventDefault();
    if (permsDraft.length === 0) return;
    setPermsSaving(true);
    try {
      await api.put(`/users/${permsFor.id}`, { permissions: permsDraft });
      toast.success(t("users.permissionsUpdated"));
      setPermsFor(null);
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setPermsSaving(false);
    }
  };

  const load = async () => {
    try {
      const { data } = await api.get("/users");
      // Web Users page = ADMIN accounts only. Mobile-only members (role
      // "member") have their own dedicated dashboard and must not appear
      // here, otherwise the list mixes web officers with mobile-only
      // brothers and confuses the admin (per user request).
      setUsers((data || []).filter((u) => u.role === "admin"));
    } catch (e) {
      setError(formatApiError(e));
    }
  };

  useEffect(() => {
    load();
    const h = () => load();
    window.addEventListener("sc-users-changed", h);
    return () => window.removeEventListener("sc-users-changed", h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      await api.post("/users", form);
      setForm(emptyForm);
      setShowForm(false);
      toast.success(t("users.createdSuccess"));
      await load();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (u) => {
    const ok = await confirm({ title: t("common.delete"), message: t("users.deleteConfirm", { name: u.username }) });
    if (!ok) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success(t("users.deletedSuccess"));
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const resetPassword = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/users/${resetFor.id}`, { password: newPassword });
      setResetFor(null);
      setNewPassword("");
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  return (
    <div data-testid="users-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="page-title">{t("users.title")}</h1>
          <p className="page-subtitle">{t("users.subtitle")}</p>
          <p className="kv-label" style={{ fontSize: "0.72rem", marginTop: "0.35rem", color: "#9a7530", fontStyle: "italic" }}>
            {t("users.webOnlyHint")}
          </p>
        </div>
        <button
          className="btn-primary-gold flex items-center gap-2"
          onClick={() => setShowForm(true)}
          data-testid="add-user-btn"
          disabled={!canManageUsers}
          style={canManageUsers ? undefined : { display: "none" }}
        >
          <Plus size={14} /> {t("users.new")}
        </button>
      </div>

      <div className="gold-divider my-8" />

      {error && <div className="error-banner mb-4">{error}</div>}

      <div className="overflow-x-auto border border-[var(--cream-200)]">
        <table className="lodge-table" data-testid="users-table">
          <thead>
            <tr>
              <th>{t("login.username")}</th>
              <th>{t("users.name")}</th>
              <th>{t("users.role")}</th>
              <th>{t("users.createdAt")}</th>
              <th className="text-right">{t("common.actions")}</th>
            </tr>
          </thead>
          <tbody>
            {users.length === 0 ? (
              <tr>
                <td colSpan={5} style={{ textAlign: "center", fontStyle: "italic", color: "#858481", padding: "2rem" }}>{t("users.noUsers")}</td>
              </tr>
            ) : users.map((u) => (
              <tr key={u.id}>
                <td style={{ fontFamily: "Cinzel, serif", fontSize: "0.9rem" }}>{u.username}</td>
                <td>{u.name}</td>
                <td>
                  <span className={`badge-grade ${u.role === "admin" ? "maestru" : ""}`}>
                    {u.office_role && ROLE_LABEL_KEYS[u.office_role]
                      ? t(ROLE_LABEL_KEYS[u.office_role])
                      : u.role === "admin" ? t("users.roleAdmin") : t("users.roleMember")}
                  </span>
                  {u.lodge_office && !u.office_role ? (
                    <div style={{ fontSize: "0.72rem", color: "#9a7530", marginTop: 4, fontStyle: "italic" }}>
                      {t(`memberProfile.lodgeOffice.${u.lodge_office}`, u.lodge_office)}
                    </div>
                  ) : null}
                </td>
                <td>{u.created_at ? new Date(u.created_at).toLocaleDateString(locale) : "—"}</td>
                <td className="text-right">
                  <div className="flex justify-end gap-2">
                    {canManageUsers && LIMITED_ROLES.includes(u.office_role) && (
                      <button
                        className="btn-outline-gold"
                        onClick={() => { setPermsFor(u); setPermsDraft(u.permissions || []); }}
                        title={t("users.editPermissions")}
                        data-testid={`edit-perms-${u.id}`}
                      >
                        <SlidersHorizontal size={12} />
                      </button>
                    )}
                    {canManageUsers && (
                      <button className="btn-outline-gold" onClick={() => setResetFor(u)} title={t("users.reset_password")} data-testid={`reset-pwd-${u.id}`}>
                        <Key size={12} />
                      </button>
                    )}
                    {canManageUsers && u.id !== user.id && (
                      <button className="btn-danger" onClick={() => remove(u)} data-testid={`delete-user-${u.id}`}>
                        <Trash2 size={12} />
                      </button>
                    )}
                    {!canManageUsers && (
                      <span style={{ color: "#9a7530", fontStyle: "italic", fontSize: "0.8rem" }}>—</span>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showForm && (
        <div className="dialog-overlay" onClick={() => setShowForm(false)}>
          <form className="dialog-panel" onClick={(e) => e.stopPropagation()} onSubmit={create} data-testid="user-form">
            <div className="flex items-center justify-between mb-4">
              <h2 className="dialog-title" style={{ margin: 0 }}>{t("users.new")}</h2>
              <button type="button" onClick={() => setShowForm(false)}><X size={20} /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="label-lodge">{t("login.username")}</label>
                <input className="input-lodge" required value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} data-testid="input-new-username" />
              </div>
              <div>
                <label className="label-lodge">{t("users.name")}</label>
                <input className="input-lodge" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="input-new-name" />
              </div>
              <div>
                <label className="label-lodge">{t("login.password")}</label>
                <input type="password" className="input-lodge" required minLength={4} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="input-new-password" />
              </div>
              <div>
                <label className="label-lodge">{t("users.role")}</label>
                <select
                  className="input-lodge"
                  value={form.office_role}
                  onChange={(e) => {
                    const r = e.target.value;
                    setForm({ ...form, office_role: r, permissions: ROLE_DEFAULTS[r] ? [...ROLE_DEFAULTS[r]] : [] });
                  }}
                  data-testid="input-new-office-role"
                >
                  {OFFICE_ROLES.map((r) => (
                    <option key={r} value={r}>{t(ROLE_LABEL_KEYS[r])}</option>
                  ))}
                </select>
              </div>
              {ROLE_DEFAULTS[form.office_role] && (
                <div data-testid="user-permissions-box">
                  <label className="label-lodge">{t("users.permissions")}</label>
                  <div className="grid grid-cols-2 gap-1 mt-1" style={{ maxHeight: 180, overflowY: "auto" }}>
                    {ALL_PERMS.map((p) => (
                      <label key={p} className="flex items-center gap-2" style={{ fontSize: "0.78rem" }}>
                        <input
                          type="checkbox"
                          checked={form.permissions.includes(p)}
                          onChange={() => setForm({
                            ...form,
                            permissions: form.permissions.includes(p)
                              ? form.permissions.filter((x) => x !== p)
                              : [...form.permissions, p],
                          })}
                          data-testid={`user-perm-${p}`}
                        />
                        <span>{t(`users.perm_${p}`)}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button type="button" className="btn-outline-gold" onClick={() => setShowForm(false)}>{t("common.cancel")}</button>
              <button type="submit" className="btn-primary-gold" disabled={saving} data-testid="save-user-btn">
                {saving ? "..." : t("common.create")}
              </button>
            </div>
          </form>
        </div>
      )}

      {permsFor && (
        <div className="dialog-overlay" onClick={() => setPermsFor(null)}>
          <form className="dialog-panel" onClick={(e) => e.stopPropagation()} onSubmit={savePerms} data-testid="edit-perms-dialog">
            <div className="flex items-center justify-between mb-4">
              <h2 className="dialog-title" style={{ margin: 0 }}>{t("users.editPermissions")}</h2>
              <button type="button" onClick={() => setPermsFor(null)}><X size={20} /></button>
            </div>
            <p style={{ color: "#34322d", marginBottom: "1rem" }}>
              {t("users.for_user")} <strong>{permsFor.username}</strong>
            </p>
            <div className="grid grid-cols-2 gap-1" style={{ maxHeight: 220, overflowY: "auto" }}>
              {ALL_PERMS.map((p) => (
                <label key={p} className="flex items-center gap-2" style={{ fontSize: "0.78rem" }}>
                  <input
                    type="checkbox"
                    checked={permsDraft.includes(p)}
                    onChange={() => setPermsDraft(
                      permsDraft.includes(p) ? permsDraft.filter((x) => x !== p) : [...permsDraft, p]
                    )}
                    data-testid={`edit-perm-${p}`}
                  />
                  <span>{t(`users.perm_${p}`)}</span>
                </label>
              ))}
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button type="button" className="btn-outline-gold" onClick={() => setPermsFor(null)}>{t("common.cancel")}</button>
              <button type="submit" className="btn-primary-gold" disabled={permsSaving || permsDraft.length === 0} data-testid="save-perms-btn">
                {permsSaving ? "..." : t("common.save")}
              </button>
            </div>
          </form>
        </div>
      )}

      {resetFor && (
        <div className="dialog-overlay" onClick={() => setResetFor(null)}>
          <form className="dialog-panel" onClick={(e) => e.stopPropagation()} onSubmit={resetPassword}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="dialog-title" style={{ margin: 0 }}>{t("users.reset_password")}</h2>
              <button type="button" onClick={() => setResetFor(null)}><X size={20} /></button>
            </div>
            <p style={{ color: "#34322d", marginBottom: "1rem" }}>
              {t("users.for_user")} <strong>{resetFor.username}</strong>
            </p>
            <div>
              <label className="label-lodge">{t("users.new_password")}</label>
              <input type="password" className="input-lodge" required minLength={4} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button type="button" className="btn-outline-gold" onClick={() => setResetFor(null)}>{t("common.cancel")}</button>
              <button type="submit" className="btn-primary-gold">{t("common.save")}</button>
            </div>
          </form>
        </div>
      )}

      {canManageUsers && <JurisdictionLodgesPanel />}
    </div>
  );
}
